'use client';

import React from 'react';
import { withPlatformComponents } from '../../platform/functions/withPlatformComponents';
import { WithPlatformComponents } from '../../platform/interfaces/WithPlatformComponents';
import { cn } from '@shared/utils';

/**
 * @internal
 */
interface ModernLinePatternProps {
  width?: number;
  height?: number;
  lineWidth?: number;
  gap?: number;
  color?: string;
  className?: string;
}

/**
 * @group Components
 * 
 * @summary
 * <blockquote>Modern decorative SVG line pattern with 3 concentric circles.</blockquote>
 * 
 * @example
 * ```tsx
 * <ModernLinePattern width={300} height={200} />
 * ```
 */
const ModernLinePatternBase: React.FC<ModernLinePatternProps & WithPlatformComponents> = ({
  width = 300,
  height = 200,
  lineWidth = 10,
  gap = 5,
  color = 'inherit',
  className,
  components
}) => {
  const { Svg, Path } = components;

  // Calculate the center of the SVG
  const cx = width / 2;
  const cy = height / 2;

  // Calculate the radii for the three concentric circles
  // Outermost circle: largest radius, then gap+lineWidth inward for each next circle
  const totalLineWidth = lineWidth * 3 + gap * 2;
  // The largest possible radius that fits all three circles inside the SVG
  const maxRadius = Math.min(width, height) / 2 - lineWidth / 2;
  const radii = [
    maxRadius,
    maxRadius - (lineWidth + gap),
    maxRadius - 2 * (lineWidth + gap)
  ];

  // Create path data for a circle using SVG arc commands (for compatibility with Path)
  const createCirclePath = (radius: number) => {
    // Start at the rightmost point of the circle
    return [
      `M ${cx + radius} ${cy}`,
      // Arc to the leftmost point
      `a ${radius} ${radius} 0 1 0 ${-2 * radius} 0`,
      // Arc back to the rightmost point
      `a ${radius} ${radius} 0 1 0 ${2 * radius} 0`
    ].join(' ');
  };

  const paths = radii.map((radius) => createCirclePath(radius));

  return (
    <Svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("text-current", className)}
    >
      {paths.map((d, i) => (
        <Path
          key={i}
          d={d}
          stroke={color}
          strokeWidth={lineWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      ))}
    </Svg>
  );
};

export const ModernLinePattern = withPlatformComponents(ModernLinePatternBase);
